import React, { useState, useEffect } from 'react';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import axios from 'axios';
import { 
  Box, 
  Typography, 
  Button, 
  TextField, 
  MenuItem, 
  Grid, 
  Card, 
  CardContent, 
  CardActions, 
  Chip, 
  IconButton, 
  InputAdornment, 
  FormControl, 
  Select, 
  InputLabel,
  Paper,
  Divider,
  Alert,
  CircularProgress,
  Snackbar,
  Stack,
  Fade,
  useTheme,
  useMediaQuery
} from '@mui/material';
import { 
  Refresh as RefreshIcon, 
  Search as SearchIcon, 
  Add as AddIcon, 
  FilterList as FilterListIcon,
  CameraAlt as CameraIcon,
  Receipt as ReceiptIcon,
  QrCodeScanner as BarcodeIcon,
  Visibility as ViewIcon,
  Edit as EditIcon,
  Clear as ClearIcon,
  ShoppingCart as ShoppingCartIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { motion } from 'framer-motion';

// Import prediction badge component
import ItemPredictionBadge from '../components/prediction/ItemPredictionBadge';

// Create an Axios instance with default configuration
const API = axios.create({
  baseURL: process.env.REACT_APP_API_URL || '', // Use environment variable or empty string
});

// Add a request interceptor to include the auth token in all requests
API.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Styled components
const StyledCard = styled(Card)(({ theme }) => ({
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  transition: 'transform 0.3s ease, box-shadow 0.3s ease',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: theme.shadows[6],
  },
}));

const CategoryChip = styled(Chip)(({ theme }) => ({
  fontWeight: 500,
  fontSize: '0.75rem',
}));

const LowStockChip = styled(Chip)(({ theme }) => ({
  backgroundColor: theme.palette.error.light,
  color: theme.palette.error.dark,
  fontWeight: 600,
  fontSize: '0.75rem',
}));

function InventoryDashboard() {
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  
  const [inventory, setInventory] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [categories, setCategories] = useState([]);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [notification, setNotification] = useState(null);
  const [showFilters, setShowFilters] = useState(!isMobile);

  // Check if we're coming from the receipt page with a refresh flag
  useEffect(() => {
    // Check for force refresh flag in localStorage
    const forceRefresh = localStorage.getItem('forceInventoryRefresh');
    if (forceRefresh === 'true') {
      console.log('Force refresh flag detected in localStorage');
      localStorage.removeItem('forceInventoryRefresh');
      setRefreshTrigger(prev => prev + 1);
    }
    
    // Check for refresh flag in location state
    if (location.state?.refresh) {
      console.log('Refresh flag detected in location state');
      
      // If we're coming from the receipt page, show a notification
      if (location.state?.fromReceipt) {
        const itemCount = location.state?.itemCount || 0;
        setNotification({
          type: 'success',
          message: `Successfully added ${itemCount} item${itemCount !== 1 ? 's' : ''} from receipt to your inventory.`
        });
      }
      
      // Trigger a refresh
      setRefreshTrigger(prev => prev + 1);
    }
  }, [location]);

  // Function to fetch inventory data
  const fetchInventoryData = async () => {
    try {
      setIsLoading(true);
      setError('');
      
      console.log('Fetching inventory data...');
      
      // Fetch inventory items with a cache-busting parameter
      const timestamp = new Date().getTime();
      const inventoryResponse = await API.get(`/api/inventory?_=${timestamp}`);
      console.log('Inventory data received:', inventoryResponse.data);
      setInventory(inventoryResponse.data);
      
      // Fetch categories
      const categoriesResponse = await API.get('/api/inventory/categories');
      console.log('Categories received:', categoriesResponse.data);
      setCategories(categoriesResponse.data);
      
      // Check if we have lastSavedItems in localStorage
      const lastSavedItems = localStorage.getItem('lastSavedItems');
      if (lastSavedItems) {
        console.log('Found lastSavedItems in localStorage:', lastSavedItems);
        // Clear it after using it
        localStorage.removeItem('lastSavedItems');
      }
      
    } catch (error) {
      console.error('Error fetching inventory data:', error);
      setError('Failed to load inventory. Please try again later.');
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch data on component mount and when refreshTrigger changes
  useEffect(() => {
    fetchInventoryData();
  }, [refreshTrigger]);

  // Update showFilters when screen size changes
  useEffect(() => {
    setShowFilters(!isMobile);
  }, [isMobile]);

  // Function to manually refresh the data
  const refreshInventory = () => {
    console.log('Manual refresh triggered');
    setRefreshTrigger(prev => prev + 1);
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleCategoryChange = (e) => {
    setSelectedCategory(e.target.value);
  };

  const resetFilters = () => {
    setSearchTerm('');
    setSelectedCategory('');
  };

  const toggleFilters = () => {
    setShowFilters(prev => !prev);
  };

  // Filter inventory based on search term and selected category
  const filteredInventory = inventory.filter(item => {
    const matchesSearch = 
      searchTerm === '' || 
      item.item_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (item.description && item.description.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (item.brand && item.brand.toLowerCase().includes(searchTerm.toLowerCase()));
    
    const matchesCategory = 
      selectedCategory === '' || 
      item.category === selectedCategory;
    
    return matchesSearch && matchesCategory;
  });

  // Get category color based on category name
  const getCategoryColor = (category) => {
    const categoryColors = {
      'Produce': 'success',
      'Dairy': 'info',
      'Meat': 'error',
      'Seafood': 'info',
      'Bakery': 'warning',
      'Pantry': 'secondary',
      'Frozen': 'primary',
      'Beverages': 'info',
      'Snacks': 'warning',
      'Household': 'default',
      'Personal Care': 'secondary',
    };
    
    return categoryColors[category] || 'default';
  };

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return '';
    
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    }).format(date);
  };

  // Check if an item is expired
  const isExpired = (dateString) => {
    if (!dateString) return false;
    
    const expiryDate = new Date(dateString);
    const today = new Date();
    
    return expiryDate < today;
  };

  return (
    <Box sx={{ width: '100%' }}>
      <Snackbar
        open={notification !== null}
        autoHideDuration={6000}
        onClose={() => setNotification(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        TransitionComponent={Fade}
      >
        <Alert 
          onClose={() => setNotification(null)} 
          severity={notification?.type || 'info'} 
          variant="filled"
          sx={{ width: '100%' }}
        >
          {notification?.message}
        </Alert>
      </Snackbar>
      
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        mb: 4,
        flexDirection: { xs: 'column', sm: 'row' },
        gap: { xs: 2, sm: 0 }
      }}>
        <Typography variant="h4" component="h1" fontWeight="bold">
          My Inventory
        </Typography>
        
        <Stack 
          direction={{ xs: 'column', sm: 'row' }} 
          spacing={1}
          sx={{ width: { xs: '100%', sm: 'auto' } }}
        >
          <Button
            variant="outlined"
            color="primary"
            startIcon={<ShoppingCartIcon />}
            component={RouterLink}
            to="/shopping-list"
            size={isMobile ? 'small' : 'medium'}
          >
            Shopping List
          </Button>
          
          <Button
            variant="outlined"
            color="primary"
            startIcon={<RefreshIcon />}
            onClick={refreshInventory}
            size={isMobile ? 'small' : 'medium'}
          >
            Refresh
          </Button>
          
          <Button
            variant="outlined"
            color="primary"
            startIcon={<BarcodeIcon />}
            component={RouterLink}
            to="/barcode-scan"
            size={isMobile ? 'small' : 'medium'}
          >
            Scan Barcode
          </Button>
          
          <Button
            variant="outlined"
            color="primary"
            startIcon={<ReceiptIcon />}
            component={RouterLink}
            to="/ocr-upload"
            size={isMobile ? 'small' : 'medium'}
          >
            Scan Receipt
          </Button>
          
          {!isTablet && (
            <Button
              variant="outlined"
              color="primary"
              startIcon={<CameraIcon />}
              component={RouterLink}
              to="/product-recognition"
              size={isMobile ? 'small' : 'medium'}
            >
              Scan Product
            </Button>
          )}
          
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            component={RouterLink}
            to="/inventory/add"
            size={isMobile ? 'small' : 'medium'}
          >
            Add Item
          </Button>
        </Stack>
      </Box>
      
      <Paper 
        elevation={2} 
        sx={{ 
          p: 2, 
          mb: 4, 
          borderRadius: 2,
          display: 'flex',
          flexDirection: 'column',
          gap: 2
        }}
      >
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
        }}>
          <Typography variant="h6" component="h2">
            Filters
          </Typography>
          
          <IconButton 
            size="small" 
            onClick={toggleFilters}
            sx={{ display: { sm: 'none' } }}
          >
            <FilterListIcon />
          </IconButton>
        </Box>
        
        {showFilters && (
          <Box sx={{ 
            display: 'flex', 
            flexDirection: { xs: 'column', sm: 'row' }, 
            gap: 2,
            alignItems: { xs: 'stretch', sm: 'center' },
          }}>
            <TextField
              label="Search Inventory"
              variant="outlined"
              fullWidth
              value={searchTerm}
              onChange={handleSearchChange}
              placeholder="Search by name, description, or brand"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
                endAdornment: searchTerm && (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={() => setSearchTerm('')}
                      edge="end"
                    >
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              size="small"
            />
            
            <FormControl 
              variant="outlined" 
              sx={{ minWidth: { xs: '100%', sm: 200 } }}
              size="small"
            >
              <InputLabel id="category-select-label">Category</InputLabel>
              <Select
                labelId="category-select-label"
                id="category-select"
                value={selectedCategory}
                onChange={handleCategoryChange}
                label="Category"
              >
                <MenuItem value="">
                  <em>All Categories</em>
                </MenuItem>
                {categories.map(category => (
                  <MenuItem key={category} value={category}>
                    {category}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            
            {(searchTerm || selectedCategory) && (
              <Button
                variant="outlined"
                color="secondary"
                onClick={resetFilters}
                startIcon={<ClearIcon />}
                size="small"
              >
                Clear Filters
              </Button>
            )}
          </Box>
        )}
      </Paper>
      
      {isLoading ? (
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          py: 8 
        }}>
          <CircularProgress />
          <Typography variant="body1" sx={{ ml: 2 }}>
            Loading your inventory...
          </Typography>
        </Box>
      ) : error ? (
        <Alert 
          severity="error" 
          sx={{ mb: 4 }}
          action={
            <Button 
              color="inherit" 
              size="small" 
              onClick={refreshInventory}
            >
              Try Again
            </Button>
          }
        >
          {error}
        </Alert>
      ) : filteredInventory.length === 0 ? (
        <Paper 
          elevation={0} 
          sx={{ 
            p: 6, 
            textAlign: 'center',
            backgroundColor: 'background.default',
            borderRadius: 2,
            border: '1px dashed',
            borderColor: 'divider'
          }}
        >
          {inventory.length === 0 ? (
            <Box>
              <Typography variant="h6" gutterBottom>
                Your inventory is empty
              </Typography>
              <Typography variant="body1" color="text.secondary" paragraph>
                Add your first item to get started!
              </Typography>
              <Button
                variant="contained"
                color="primary"
                startIcon={<AddIcon />}
                component={RouterLink}
                to="/inventory/add"
                sx={{ mt: 2 }}
              >
                Add Your First Item
              </Button>
            </Box>
          ) : (
            <Typography variant="body1" color="text.secondary">
              No items match your current filters. Try changing your search or category selection.
            </Typography>
          )}
        </Paper>
      ) : (
        <Grid container spacing={3}>
          {filteredInventory.map(item => (
            <Grid item xs={12} sm={6} md={4} lg={3} key={item.item_id}>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3 }}
              >
                <StyledCard>
                  <CardContent sx={{ flexGrow: 1 }}>
                    <Box sx={{ 
                      display: 'flex', 
                      justifyContent: 'space-between', 
                      alignItems: 'flex-start',
                      mb: 1
                    }}>
                      <Typography variant="h6" component="h3" noWrap title={item.item_name}>
                        {item.item_name}
                      </Typography>
                      
                      {item.low_stock_threshold && item.quantity <= item.low_stock_threshold && (
                        <LowStockChip 
                          label="Low Stock" 
                          size="small"
                        />
                      )}
                    </Box>
                    
                    <Box sx={{ mb: 2 }}>
                      <CategoryChip 
                        label={item.category} 
                        size="small" 
                        color={getCategoryColor(item.category)}
                      />
                    </Box>
                    
                    <Typography variant="body1" sx={{ mb: 1, fontWeight: 500 }}>
                      {item.quantity} {item.unit_of_measure}
                    </Typography>
                    
                    {/* Add prediction badge */}
                    <ItemPredictionBadge itemId={item.item_id} />
                    
                    {item.description && (
                      <Typography 
                        variant="body2" 
                        color="text.secondary" 
                        sx={{ 
                          mb: 1,
                          mt: 1,
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis'
                        }}
                      >
                        {item.description}
                      </Typography>
                    )}
                    
                    {item.brand && (
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        <strong>Brand:</strong> {item.brand}
                      </Typography>
                    )}
                    
                    {item.expiry_date && (
                      <Typography 
                        variant="body2" 
                        color={isExpired(item.expiry_date) ? "error.main" : "text.secondary"}
                        sx={{ fontWeight: isExpired(item.expiry_date) ? 500 : 400 }}
                      >
                        <strong>Expires:</strong> {formatDate(item.expiry_date)}
                        {isExpired(item.expiry_date) && " (Expired)"}
                      </Typography>
                    )}
                  </CardContent>
                  
                  <Divider />
                  
                  <CardActions sx={{ justifyContent: 'flex-end', p: 1 }}>
                    <Button
                      component={RouterLink}
                      to={`/inventory/${item.item_id}`}
                      size="small"
                      startIcon={<ViewIcon />}
                    >
                      View
                    </Button>
                    <Button
                      component={RouterLink}
                      to={`/inventory/edit/${item.item_id}`}
                      size="small"
                      color="primary"
                      startIcon={<EditIcon />}
                    >
                      Edit
                    </Button>
                  </CardActions>
                </StyledCard>
              </motion.div>
            </Grid>
          ))}
        </Grid>
      )}
    </Box>
  );
}

export default InventoryDashboard;